import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { primaryTint } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import AgentBuilderRoutes from 'components/@home/@settings/Tabs/Agents/AgentBuilderRoutes';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import AttachSkillsDialog from 'components/@home/@settings/Tabs/Agents/AttachSkillsDialog';
import AttachToolsDialog from 'components/@home/@settings/Tabs/Agents/AttachToolsDialog';
import ManageAgentKnowledgeDialog from 'components/@home/@settings/Tabs/Agents/ManageAgentKnowledgeDialog';
import ManageMetricsDialog from 'components/@home/@settings/Tabs/Agents/ManageMetricsDialog';
import ManageTagsDialog from 'components/@home/@settings/Tabs/Agents/ManageTagsDialog';
import { INSTRUCTION_KINDS } from 'components/@home/@settings/Tabs/Agents/panels/InstructionsPanel';
import OverflowTip from 'components/common/OverflowTip';
import AddIcon from 'mdi-react/AddIcon';
import ChartLineIcon from 'mdi-react/ChartLineIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import CodeTagsIcon from 'mdi-react/CodeTagsIcon';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import FileTreeOutlineIcon from 'mdi-react/FileTreeOutlineIcon';
import TagMultipleOutlineIcon from 'mdi-react/TagMultipleOutlineIcon';
import TextBoxOutlineIcon from 'mdi-react/TextBoxOutlineIcon';
import WebhookIcon from 'mdi-react/WebhookIcon';
import DatabaseOutlineIcon from 'mdi-react/DatabaseOutlineIcon';
import WrenchOutlineIcon from 'mdi-react/WrenchOutlineIcon';
import { bool, func, node, object, string } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { buildAgenticPath, useAgenticRoute } from 'utils/agenticRoute';
import { lifecycleCatalogTools } from 'utils/agentGlobalTools';
import {
  attachedSkillsForAgent,
  catalogKnowledgeRows,
  unionToolsForAgent,
} from 'utils/skillToolsUnion';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  layout: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  nav: {
    width: 240,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: 'auto',
    padding: theme.spacing(1, 0, 2),
    backgroundColor: theme.palette.background.paper,
  },
  navGroup: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 2, 0.5),
  },
  navRow: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: 34,
    borderRadius: '6px',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    gap: theme.spacing(0.25),
    '&:hover .addBtn': { opacity: 1 },
  },
  chevronSlot: {
    width: 22,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  rowIcon: {
    color: theme.palette.text.secondary,
    display: 'inline-flex',
    alignItems: 'center',
    width: 20,
    marginRight: theme.spacing(0.5),
    flexShrink: 0,
  },
  rowLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  addBtn: {
    padding: '4px',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    opacity: 0,
    transition: 'opacity 120ms ease, background-color 120ms ease, color 120ms ease',
    '&:hover': {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      backgroundColor: primaryTint(theme, 0.06),
    },
  },
  subItem: {
    paddingLeft: theme.spacing(4.5),
    paddingRight: theme.spacing(1),
    minHeight: 30,
    borderRadius: '6px',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    gap: theme.spacing(0.75),
  },
  subIcon: {
    color: theme.palette.text.secondary,
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  subLabel: {
    fontSize: theme.typography.pxToRem(12.5),
    color: theme.palette.text.primary,
  },
  emptyHint: {
    paddingLeft: theme.spacing(4.5),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  body: {
    flex: '1 1 0',
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  routeOutlet: {
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
});

const SimpleRow = ({ styles, label, icon = null, selected = false, onClick }) => (
  <ListItemButton selected={selected} sx={styles.navRow} onClick={onClick}>
    <Box component="span" sx={styles.chevronSlot} />
    <Box component="span" sx={styles.rowIcon}>
      {icon}
    </Box>
    <ListItemText primary={label} slotProps={{ primary: { sx: styles.rowLabel } }} />
  </ListItemButton>
);

const CollapsibleRow = ({
  styles,
  label,
  icon = null,
  selected = false,
  expanded = false,
  onToggle,
  onClick,
  onAdd = null,
  children = null,
  hasItems = false,
  emptyHint = '',
}) => (
  <>
    <ListItemButton
      selected={selected && !expanded}
      sx={styles.navRow}
      onClick={() => {
        onClick();
        if (!expanded && hasItems) onToggle(true);
      }}
    >
      <Box
        component="span"
        sx={styles.chevronSlot}
        role="button"
        tabIndex={-1}
        onClick={e => {
          e.stopPropagation();
          onToggle(!expanded);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onToggle(!expanded);
          }
        }}
      >
        {expanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
      </Box>
      <Box component="span" sx={styles.rowIcon}>
        {icon}
      </Box>
      <ListItemText primary={label} slotProps={{ primary: { sx: styles.rowLabel } }} />
      {onAdd && (
        <Tooltip title={I18n.t('Common.Add')}>
          <IconButton
            className="addBtn"
            size="small"
            sx={styles.addBtn}
            onClick={e => {
              e.stopPropagation();
              onAdd();
            }}
          >
            <AddIcon size={16} />
          </IconButton>
        </Tooltip>
      )}
    </ListItemButton>
    <Collapse in={expanded} unmountOnExit>
      {hasItems ? children : <Box sx={styles.emptyHint}>{emptyHint}</Box>}
    </Collapse>
  </>
);

const ICON_FOR = {
  overview: <CogOutlineIcon size={16} />,
  instructions: <TextBoxOutlineIcon size={16} />,
  skills: <FileTreeOutlineIcon size={16} />,
  tools: <WrenchOutlineIcon size={16} />,
  knowledge: <DatabaseOutlineIcon size={16} />,
  tags: <TagMultipleOutlineIcon size={16} />,
  metrics: <ChartLineIcon size={16} />,
};

const NAV_BUILD = [
  { id: 'instructions', labelKey: 'NavInstructions', type: 'collapsible' },
  { id: 'skills', labelKey: 'NavSkills', type: 'collapsible' },
  { id: 'tools', labelKey: 'NavTools', type: 'collapsible' },
  { id: 'knowledge', labelKey: 'NavKnowledge', type: 'collapsible' },
];

const NAV_ANALYZE = [
  { id: 'tags', labelKey: 'NavTags', type: 'collapsible' },
  { id: 'metrics', labelKey: 'NavMetrics', type: 'collapsible' },
];

const INSTRUCTION_KIND_LABELS = {
  base: 'InstructionBaseTitle',
  tags: 'InstructionTagsTitle',
  summary: 'InstructionSummaryTitle',
};

const NAV_CONFIG = [{ id: 'overview', labelKey: 'NavSettings', type: 'simple' }];

const AgentBuilderShell = () => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const navigate = useNavigate();
  const {
    flow,
    authCompany,
    agent,
    skills,
    tools,
    knowledge,
    tags,
    metrics,
    onSaveAgent,
    onCreateSkill,
    onCreateTool,
    onCreateKnowledge,
  } = useAgentEdit();
  const route = useAgenticRoute(authCompany);
  const section = route.section || 'instructions';
  const instructionKind = route.instructionKind || 'base';

  const attachedSkills = useMemo(() => attachedSkillsForAgent(agent, skills), [agent, skills]);

  const { tools: unionTools, builtins: unionBuiltins } = useMemo(
    () => unionToolsForAgent(agent, attachedSkills, tools),
    [agent, attachedSkills, tools],
  );

  const knowledgeList = useMemo(() => catalogKnowledgeRows(knowledge, skills), [knowledge, skills]);

  const lifecycleCatalog = useMemo(() => lifecycleCatalogTools(tools), [tools]);

  const [expanded, setExpanded] = useState({
    instructions: section === 'instructions',
    skills: section === 'skills',
    tools: section === 'tools',
    knowledge: section === 'knowledge',
    tags: section === 'tags',
    metrics: section === 'metrics',
  });

  const attachedTags = useMemo(() => {
    const ids = (agent?.tagIds || []).map(String);
    if (!ids.length) return [];
    const byId = new Map((tags || []).map(t => [String(t._id), t]));
    return ids.map(id => byId.get(id)).filter(Boolean);
  }, [agent?.tagIds, tags]);

  const attachedMetrics = useMemo(() => {
    const ids = (agent?.metricIds || []).map(String);
    if (!ids.length) return [];
    const byId = new Map((metrics || []).map(m => [String(m._id), m]));
    return ids.map(id => byId.get(id)).filter(Boolean);
  }, [agent?.metricIds, metrics]);

  const [attachSkillsOpen, setAttachSkillsOpen] = useState(false);
  const [attachToolsOpen, setAttachToolsOpen] = useState(false);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [manageMetricsOpen, setManageMetricsOpen] = useState(false);
  const [manageKnowledgeOpen, setManageKnowledgeOpen] = useState(false);

  const setExpand = (key, val) =>
    setExpanded(prev => ({ ...prev, [key]: typeof val === 'boolean' ? val : !prev[key] }));

  const currentSkillId = route.skillId ? String(route.skillId) : null;
  const currentToolId = route.toolId ? String(route.toolId) : null;
  const currentKnowledgeId = route.knowledgeId ? String(route.knowledgeId) : null;
  const currentTagId = route.tagId ? String(route.tagId) : null;
  const currentMetricId = route.metricId ? String(route.metricId) : null;
  const inlineEditorOpen = Boolean(
    currentSkillId || currentToolId || currentKnowledgeId || currentTagId || currentMetricId,
  );

  const navigateSection = (next, extra = {}) => {
    if (!flow?._id) return;
    navigate(
      buildAgenticPath(authCompany, {
        flowId: flow._id,
        section: next,
        ...extra,
      }),
    );
  };

  const selectSection = next => {
    navigateSection(next, {
      instructionKind: next === 'instructions' ? instructionKind : undefined,
    });
  };

  const setInstructionKind = kind => {
    navigateSection('instructions', { instructionKind: kind });
  };

  const openSkill = id => navigateSection('skills', { skillId: id });
  const openTool = id => navigateSection('tools', { toolId: id });
  const openKnowledge = id => navigateSection('knowledge', { knowledgeId: id });
  const openTag = id => navigateSection('tags', { tagId: id });
  const openMetric = id => navigateSection('metrics', { metricId: id });

  const renderItem = item => {
    const label = I18n.t(`AgenticChatFlow.${item.labelKey}`);
    const icon = ICON_FOR[item.id];
    if (item.type === 'collapsible' && item.id === 'instructions') {
      return (
        <CollapsibleRow
          key={item.id}
          styles={styles}
          label={label}
          icon={icon}
          selected={section === 'instructions'}
          expanded={expanded.instructions}
          onToggle={v => setExpand('instructions', v)}
          onClick={() => selectSection('instructions')}
          hasItems
        >
          {INSTRUCTION_KINDS.map(kind => (
            <ListItemButton
              key={kind}
              selected={section === 'instructions' && instructionKind === kind}
              sx={styles.subItem}
              onClick={() => setInstructionKind(kind)}
            >
              <ListItemText
                primary={I18n.t(`AgenticChatFlow.${INSTRUCTION_KIND_LABELS[kind]}`)}
                slotProps={{ primary: { sx: styles.subLabel } }}
              />
            </ListItemButton>
          ))}
        </CollapsibleRow>
      );
    }
    if (item.type === 'collapsible' && item.id === 'skills') {
      return (
        <CollapsibleRow
          key={item.id}
          styles={styles}
          label={label}
          icon={icon}
          selected={section === 'skills' && !currentSkillId}
          expanded={expanded.skills}
          onToggle={v => setExpand('skills', v)}
          onClick={() => selectSection('skills')}
          onAdd={() => setAttachSkillsOpen(true)}
          hasItems={attachedSkills.length > 0}
          emptyHint={I18n.t('AgenticChatFlow.NoAttachedSkills')}
        >
          {attachedSkills.map(skill => (
            <ListItemButton
              key={skill._id}
              selected={currentSkillId === String(skill._id)}
              sx={styles.subItem}
              onClick={() => openSkill(skill._id)}
            >
              <ListItemText primary={<OverflowTip value={skill.displayName || skill.name} />} />
            </ListItemButton>
          ))}
        </CollapsibleRow>
      );
    }
    if (item.type === 'collapsible' && item.id === 'tags') {
      return (
        <CollapsibleRow
          key={item.id}
          styles={styles}
          label={label}
          icon={icon}
          selected={section === 'tags' && !currentTagId}
          expanded={expanded.tags}
          onToggle={v => setExpand('tags', v)}
          onClick={() => selectSection('tags')}
          onAdd={() => setManageTagsOpen(true)}
          hasItems={attachedTags.length > 0}
          emptyHint={I18n.t('AgenticChatFlow.NoAttachedTags')}
        >
          {attachedTags.map(tag => (
            <ListItemButton
              key={tag._id}
              selected={currentTagId === String(tag._id)}
              sx={styles.subItem}
              onClick={() => openTag(tag._id)}
            >
              <Box component="span" sx={styles.subIcon}>
                <TagMultipleOutlineIcon size={14} />
              </Box>
              <ListItemText primary={<OverflowTip value={tag.name} />} />
            </ListItemButton>
          ))}
        </CollapsibleRow>
      );
    }
    if (item.type === 'collapsible' && item.id === 'metrics') {
      return (
        <CollapsibleRow
          key={item.id}
          styles={styles}
          label={label}
          icon={icon}
          selected={section === 'metrics' && !currentMetricId}
          expanded={expanded.metrics}
          onToggle={v => setExpand('metrics', v)}
          onClick={() => selectSection('metrics')}
          onAdd={() => setManageMetricsOpen(true)}
          hasItems={attachedMetrics.length > 0}
          emptyHint={I18n.t('AgenticChatFlow.NoAttachedMetrics')}
        >
          {attachedMetrics.map(metric => (
            <ListItemButton
              key={metric._id}
              selected={currentMetricId === String(metric._id)}
              sx={styles.subItem}
              onClick={() => openMetric(metric._id)}
            >
              <Box component="span" sx={styles.subIcon}>
                <ChartLineIcon size={14} />
              </Box>
              <ListItemText primary={<OverflowTip value={metric.name} />} />
            </ListItemButton>
          ))}
        </CollapsibleRow>
      );
    }
    if (item.type === 'collapsible' && item.id === 'knowledge') {
      return (
        <CollapsibleRow
          key={item.id}
          styles={styles}
          label={label}
          icon={icon}
          selected={section === 'knowledge' && !currentKnowledgeId}
          expanded={expanded.knowledge}
          onToggle={v => setExpand('knowledge', v)}
          onClick={() => selectSection('knowledge')}
          onAdd={() => setManageKnowledgeOpen(true)}
          hasItems={knowledgeList.length > 0}
          emptyHint={I18n.t('AgenticChatFlow.EmptyKnowledge.Title')}
        >
          {knowledgeList.map(k => (
            <ListItemButton
              key={k._id}
              selected={currentKnowledgeId === String(k._id)}
              sx={styles.subItem}
              onClick={() => openKnowledge(k._id)}
            >
              <Box component="span" sx={styles.subIcon}>
                <DatabaseOutlineIcon size={14} />
              </Box>
              <ListItemText primary={<OverflowTip value={k.displayName || k.name} />} />
            </ListItemButton>
          ))}
        </CollapsibleRow>
      );
    }
    if (item.type === 'collapsible' && item.id === 'tools') {
      return (
        <CollapsibleRow
          key={item.id}
          styles={styles}
          label={label}
          icon={icon}
          selected={section === 'tools' && !currentToolId}
          expanded={expanded.tools}
          onToggle={v => setExpand('tools', v)}
          onClick={() => selectSection('tools')}
          onAdd={() => setAttachToolsOpen(true)}
          hasItems={unionTools.length + unionBuiltins.length > 0}
          emptyHint={I18n.t('AgenticChatFlow.NoAttachedTools')}
        >
          {unionBuiltins.map(row => (
            <ListItemButton
              key={row._id}
              selected={currentToolId === String(row._id)}
              sx={styles.subItem}
              onClick={() => openTool(row._id)}
            >
              <Box component="span" sx={styles.subIcon}>
                <CodeTagsIcon size={14} />
              </Box>
              <ListItemText primary={<OverflowTip value={row.name} />} />
            </ListItemButton>
          ))}
          {unionTools.map(tool => (
            <ListItemButton
              key={tool._id}
              selected={currentToolId === String(tool._id)}
              sx={styles.subItem}
              onClick={() => openTool(tool._id)}
            >
              <Box component="span" sx={styles.subIcon}>
                {tool.kind === 'http' ? <WebhookIcon size={14} /> : <CodeTagsIcon size={14} />}
              </Box>
              <ListItemText primary={<OverflowTip value={tool.name} />} />
            </ListItemButton>
          ))}
        </CollapsibleRow>
      );
    }
    return (
      <SimpleRow
        key={item.id}
        styles={styles}
        label={label}
        icon={icon}
        selected={section === item.id && !inlineEditorOpen}
        onClick={() => selectSection(item.id)}
      />
    );
  };

  const renderNavGroup = (titleKey, items) => (
    <>
      <Typography sx={styles.navGroup}>{I18n.t(`AgenticChatFlow.${titleKey}`)}</Typography>
      <List disablePadding dense>
        {items.map(renderItem)}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={styles.layout}>
        <Box sx={styles.nav} component="nav">
          {renderNavGroup('NavBuild', NAV_BUILD)}
          {renderNavGroup('NavAnalyze', NAV_ANALYZE)}
          {renderNavGroup('NavConfigure', NAV_CONFIG)}
        </Box>
        <Box
          sx={{
            ...styles.body,
            ...(inlineEditorOpen
              ? {
                  padding: 0,
                  backgroundColor: 'background.paper',
                  overflow: 'hidden',
                }
              : null),
          }}
        >
          <Box sx={styles.routeOutlet}>
            <AgentBuilderRoutes />
          </Box>
        </Box>
      </Box>
      <AttachSkillsDialog
        open={attachSkillsOpen}
        onClose={() => setAttachSkillsOpen(false)}
        skills={skills}
        selectedIds={agent?.skillIds || []}
        onSave={skillIds => onSaveAgent({ skillIds })}
        onCreateSkill={onCreateSkill}
      />
      <AttachToolsDialog
        open={attachToolsOpen}
        onClose={() => setAttachToolsOpen(false)}
        tools={lifecycleCatalog}
        selectedAttachments={agent?.attachedTools || []}
        onSave={next => onSaveAgent({ attachedTools: next })}
        onCreateTool={onCreateTool}
        dialogTitle={I18n.t('AgenticChatFlow.AttachGlobalToolsTitle')}
        catalogBuiltinFilter={b => b.name === 'announce'}
      />
      <ManageTagsDialog
        open={manageTagsOpen}
        tags={tags || []}
        attachedIds={(agent?.tagIds || []).map(String)}
        onClose={() => setManageTagsOpen(false)}
        onSaveAttachments={tagIds => onSaveAgent({ tagIds })}
        onCreateTag={() => {
          setManageTagsOpen(false);
          openTag('new');
        }}
      />
      <ManageMetricsDialog
        open={manageMetricsOpen}
        metrics={metrics || []}
        attachedIds={(agent?.metricIds || []).map(String)}
        onClose={() => setManageMetricsOpen(false)}
        onSaveAttachments={metricIds => onSaveAgent({ metricIds })}
        onCreateMetric={() => {
          setManageMetricsOpen(false);
          openMetric('new');
        }}
      />
      <ManageAgentKnowledgeDialog
        open={manageKnowledgeOpen}
        onClose={() => setManageKnowledgeOpen(false)}
        knowledge={knowledge || []}
        attachedSkills={attachedSkills}
        onCreateKnowledge={() => {
          setManageKnowledgeOpen(false);
          onCreateKnowledge?.();
        }}
      />
    </Box>
  );
};

SimpleRow.propTypes = {
  styles: object.isRequired,
  label: string.isRequired,
  icon: node,
  selected: bool,
  onClick: func.isRequired,
};

CollapsibleRow.propTypes = {
  styles: object.isRequired,
  label: string.isRequired,
  icon: node,
  selected: bool,
  expanded: bool,
  onToggle: func.isRequired,
  onClick: func.isRequired,
  onAdd: func,
  children: node,
  hasItems: bool,
  emptyHint: string,
};

export default AgentBuilderShell;
